<template>
  <div v-if="visible" class="sheet-scrim share-scrim" @click.self="close">
    <div class="sheet share-card" role="dialog" aria-modal="true" aria-labelledby="shareTitle">
      <div class="sheet-head">
        <h2 id="shareTitle" class="sheet-title">{{ hasScore ? 'Challenge a friend' : 'Share this sentence' }}</h2>
        <button type="button" class="icon-btn icon-btn-filled icon-btn-sm" @click="close" aria-label="Close">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="sheet-body share-body">

      <div class="share-preview">
        <p v-if="payload.sentence" class="share-quote">&ldquo;{{ payload.sentence }}&rdquo;</p>
        <p v-if="hasScore" class="share-score">
          <i class="fas fa-bullseye"></i> You scored {{ payload.score }}%
        </p>
      </div>

      <div class="share-link-row">
        <span class="share-link" :title="url">{{ url }}</span>
        <button type="button" class="btn btn-secondary btn-sm share-copy" :class="{ 'is-copied': copied }" @click="copy">
          <i class="fas" :class="copied ? 'fa-check' : 'fa-copy'"></i>
          {{ copied ? 'Copied' : 'Copy' }}
        </button>
      </div>

      <!-- The only path that sends an attempt somewhere durable, so the
           consequences are stated before the toggle. Off by default. -->
      <div v-if="payload.canAttachVoice" class="share-voice">
        <label class="share-voice-row">
          <input type="checkbox" :checked="voiceOn" :disabled="uploading" @change="onVoiceToggle">
          <span class="share-voice-label">
            <i class="fas fa-microphone"></i> Attach my voice
          </span>
          <i v-if="uploading" class="fas fa-circle-notch fa-spin share-voice-spinner"></i>
        </label>
        <p class="share-voice-note">
          They can hear your attempt and try to beat it. Anyone with the link can play it,
          and it is deleted after 90 days. You can remove it anytime in Settings.
        </p>
        <p v-if="voiceError" class="share-voice-error">{{ voiceError }}</p>
      </div>

      <button v-if="canNativeShare" type="button" class="btn btn-primary btn-block" @click="nativeShare">
        <i class="fas fa-share-alt"></i> Share
      </button>

      <div class="share-targets">
        <a
          v-for="target in targets"
          :key="target.name"
          class="icon-btn icon-btn-filled share-target"
          :href="target.href"
          target="_blank"
          rel="noopener noreferrer"
          :title="target.name"
          @click="trackTarget(target.label)"
        ><i class="fab" :class="target.icon"></i></a>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
import { buildShareUrl, shareText, shareTitle, copyToClipboard } from '../../services/share';
import { uploadAttempt, deleteShared, rememberShared } from '../../services/shareVoice';

const COPIED_MS = 2000;

export default {
  name: 'ShareSheet',
  data() {
    return {
      copied: false,
      voiceOn: false,
      uploading: false,
      voiceError: '',
      audioId: null,
      deleteToken: null
    };
  },
  computed: {
    visible() {
      return this.$store.state.settings.showShareSheet;
    },
    // Never null while visible: openShareSheet refuses a payload without videoId.
    payload() {
      return this.$store.state.settings.sharePayload || {};
    },
    hasScore() {
      return this.payload.score !== null && this.payload.score !== undefined;
    },
    url() {
      const p = this.payload;
      return buildShareUrl({
        videoId: p.videoId,
        start: p.start,
        lang: p.lang,
        motherTongue: p.motherTongue,
        score: p.score,
        name: p.name,
        sentence: p.sentence,
        audioId: this.audioId
      }) || '';
    },
    text() {
      return shareText(this.payload);
    },
    canNativeShare() {
      return typeof navigator !== 'undefined' && !!navigator.share;
    },
    targets() {
      const u = encodeURIComponent(this.url);
      const t = encodeURIComponent(this.text);
      return [
        // fa-twitter: the pinned Font Awesome 5.15.4 has no X glyph.
        { name: 'X', label: 'x', icon: 'fa-twitter', href: 'https://twitter.com/intent/tweet?text=' + t + '&url=' + u },
        { name: 'Facebook', label: 'facebook', icon: 'fa-facebook-f', href: 'https://www.facebook.com/sharer/sharer.php?u=' + u },
        // URL on its own line: run into the sentence, auto-linkers misjudge its end.
        { name: 'WhatsApp', label: 'whatsapp', icon: 'fa-whatsapp', href: 'https://wa.me/?text=' + encodeURIComponent(this.text + '\n' + this.url) },
        { name: 'Telegram', label: 'telegram', icon: 'fa-telegram-plane', href: 'https://t.me/share/url?url=' + u + '&text=' + t }
      ];
    }
  },
  watch: {
    // Reset per open, or a stale audioId attaches the wrong recording.
    visible(isVisible) {
      if (isVisible) this.reset();
    }
  },
  beforeDestroy() {
    if (this._copyTimer) clearTimeout(this._copyTimer);
  },
  methods: {
    reset() {
      this.copied = false;
      this.voiceOn = false;
      this.uploading = false;
      this.voiceError = '';
      this.audioId = null;
      this.deleteToken = null;
    },

    close() {
      this.$store.dispatch('settings/closeShareSheet');
    },

    copy() {
      const self = this;
      copyToClipboard(this.url).then(function(ok) {
        if (!ok) return;
        self.copied = true;
        self.track('copy');
        if (self._copyTimer) clearTimeout(self._copyTimer);
        self._copyTimer = setTimeout(function() { self.copied = false; }, COPIED_MS);
      });
    },

    // Nothing awaited before navigator.share: iOS Safari only honours it inside
    // the user gesture. Also why the link is built locally.
    nativeShare() {
      const self = this;
      navigator.share({
        title: shareTitle(this.payload),
        text: this.text,
        url: this.url
      }).then(function() {
        self.track('native');
        self.close();
      }, function() {
        // Cancelling the native sheet rejects; not an error.
      });
    },

    onVoiceToggle(event) {
      if (event.target.checked) this.attachVoice();
      else this.detachVoice();
    },

    attachVoice() {
      const blob = this.payload.getRecording ? this.payload.getRecording() : null;
      if (!blob) {
        this.voiceError = 'That recording is no longer available.';
        this.voiceOn = false;
        return;
      }

      const self = this;
      this.uploading = true;
      this.voiceError = '';
      uploadAttempt(blob).then(function(result) {
        self.uploading = false;
        self.voiceOn = true;
        self.audioId = result.id;
        self.deleteToken = result.deleteToken;
        // Remembered on upload: from then on the user must be able to revoke it.
        rememberShared({
          id: result.id,
          deleteToken: result.deleteToken,
          sentence: self.payload.sentence || ''
        });
        self.track('voice_attached');
      }, function(err) {
        self.uploading = false;
        self.voiceOn = false;
        self.voiceError = self.voiceMessage(err && err.code);
      });
    },

    detachVoice() {
      const id = this.audioId;
      const token = this.deleteToken;
      this.voiceOn = false;
      this.audioId = null;
      this.deleteToken = null;
      this.voiceError = '';
      if (id && token) deleteShared(id, token);
    },

    voiceMessage(code) {
      if (code === 'RATE_LIMITED') return 'Too many uploads just now. Try again in a minute.';
      if (code === 'TOO_LARGE') return 'That recording is too long to attach.';
      if (code === 'UNSUPPORTED_TYPE') return 'This browser records in a format we cannot share.';
      if (code === 'TIMEOUT' || code === 'NETWORK') return 'Could not reach the server. Check your connection.';
      return 'Could not attach your voice. The link still works without it.';
    },

    trackTarget(label) {
      this.track(label);
    },

    track(label) {
      if (window.$analytics) {
        window.$analytics.trackEvent('clip_shared', {
          category: 'Share',
          label: label,
          value: this.hasScore ? this.payload.score : null,
          extraParams: {
            videoId: this.payload.videoId,
            withVoice: !!this.audioId
          }
        });
      }
    }
  }
}
</script>

<style scoped>
/* Above the action bar (1000), below the tutorial (2000). */
.share-scrim { z-index: 1500; }

.share-card { max-width: 440px; }

.share-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.share-preview {
  background: var(--surface-2);
  border-radius: var(--r-card);
  padding: 14px 16px;
}

.share-quote {
  margin: 0;
  font-size: 16px;
  line-height: 1.45;
  color: var(--text);
}

.share-score {
  margin: 8px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--accent);
}

.share-link-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--field);
  border-radius: var(--r-control);
  padding: 4px 4px 4px 14px;
}

.share-link {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: var(--muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.share-copy {
  flex: none;
  background: var(--surface-2);
}

.share-copy.is-copied {
  background: var(--pass-soft);
  color: var(--pass);
}

.share-voice {
  border-top: 1px solid var(--line);
  padding-top: 14px;
}

.share-voice-row {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.share-voice-row input {
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: var(--accent);
}

.share-voice-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
}

.share-voice-spinner {
  color: var(--accent);
  font-size: 13px;
}

.share-voice-note {
  margin: 6px 0 0 28px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--muted);
}

.share-voice-error {
  margin: 6px 0 0 28px;
  font-size: 13px;
  color: var(--fail);
}

.share-targets {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.share-target {
  text-decoration: none;
}
.share-target:hover {
  background: var(--accent-soft);
  color: var(--accent);
}
</style>
